// Unified MUI v5 Theme with Dark Mode Support
// Created as part of ENG2-1698: Create Unified MUI v5 Theme
// Updated to support light/dark mode switching

import { createTheme, ThemeOptions } from '@mui/material/styles';
import { tokens, darkModeColors, lightModeColors } from './tokens';

// Shared theme options (same for light and dark)
const getBaseThemeOptions = (): Partial<ThemeOptions> => ({
  typography: {
    fontFamily: '"Lato", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Montserrat", "Helvetica", sans-serif',
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontFamily: '"Montserrat", "Helvetica", sans-serif',
      fontWeight: 700,
      fontSize: '2rem',
    },
    h3: {
      fontFamily: '"Montserrat", "Helvetica", sans-serif',
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h4: {
      fontFamily: '"Montserrat", "Helvetica", sans-serif',
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h5: {
      fontFamily: '"Montserrat", "Helvetica", sans-serif',
      fontWeight: 600,
      fontSize: '1.125rem',
    },
    h6: {
      fontFamily: '"Montserrat", "Helvetica", sans-serif',
      fontWeight: 600,
      fontSize: '1rem',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
      fontSize: '0.8125rem',
    },
    caption: {
      fontSize: '0.75rem',
    },
  },
  shape: {
    borderRadius: tokens.radius.md,
  },
});

// Light theme options
const getLightThemeOptions = (): ThemeOptions => ({
  ...getBaseThemeOptions(),
  palette: {
    mode: 'light',
    primary: {
      main: tokens.colors.primary[500],
      light: tokens.colors.primary[300],
      dark: tokens.colors.primary[700],
      contrastText: tokens.colors.grey[900],
    },
    secondary: {
      main: tokens.colors.accent[500],
      light: tokens.colors.accent[400],
      dark: '#F57C1F',
    },
    success: {
      main: tokens.colors.success[500],
      dark: tokens.colors.success[600],
      light: tokens.colors.success[100],
    },
    warning: {
      main: tokens.colors.warning[500],
      light: tokens.colors.warning[100],
    },
    error: {
      main: tokens.colors.error[500],
      dark: tokens.colors.error[600],
      light: tokens.colors.error[100],
    },
    info: {
      main: tokens.colors.info[500],
      light: tokens.colors.info[100],
    },
    grey: tokens.colors.grey,
    background: {
      default: lightModeColors.background.default,
      paper: lightModeColors.background.paper,
    },
    text: {
      primary: lightModeColors.text.primary,
      secondary: lightModeColors.text.secondary,
    },
    divider: lightModeColors.border.primary,
  },
  shadows: [
    'none',
    tokens.shadows.xs,
    tokens.shadows.sm,
    tokens.shadows.md,
    tokens.shadows.lg,
    ...Array(20).fill(tokens.shadows.lg),
  ] as ThemeOptions['shadows'],
  components: getLightComponents(),
});

// Dark theme options
const getDarkThemeOptions = (): ThemeOptions => ({
  ...getBaseThemeOptions(),
  palette: {
    mode: 'dark',
    primary: {
      main: tokens.colors.primary[500],
      light: tokens.colors.primary[300],
      dark: tokens.colors.primary[700],
      contrastText: tokens.colors.grey[900],
    },
    secondary: {
      main: tokens.colors.accent[400],
      light: tokens.colors.accent[300],
      dark: tokens.colors.accent[500],
    },
    success: {
      main: tokens.colors.success[500],
      dark: tokens.colors.success[600],
      light: tokens.colors.success[300],
    },
    warning: {
      main: tokens.colors.warning[500],
      light: tokens.colors.warning[300],
    },
    error: {
      main: tokens.colors.error[500],
      dark: tokens.colors.error[600],
      light: tokens.colors.error[300],
    },
    info: {
      main: tokens.colors.info[500],
      light: '#64B5F6',
    },
    grey: tokens.colors.grey,
    background: {
      default: darkModeColors.background.default,
      paper: darkModeColors.background.paper,
    },
    text: {
      primary: darkModeColors.text.primary,
      secondary: darkModeColors.text.secondary,
    },
    divider: darkModeColors.border.primary,
    action: {
      hover: darkModeColors.hover,
      selected: darkModeColors.selected,
    },
  },
  shadows: [
    'none',
    tokens.darkShadows.xs,
    tokens.darkShadows.sm,
    tokens.darkShadows.md,
    tokens.darkShadows.lg,
    ...Array(20).fill(tokens.darkShadows.lg),
  ] as ThemeOptions['shadows'],
  components: getDarkComponents(),
});

// Light mode component overrides
const getLightComponents = (): ThemeOptions['components'] => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: tokens.colors.grey[100],
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: tokens.colors.grey[400],
          borderRadius: '4px',
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.sm,
        padding: '8px 16px',
        fontSize: '13px',
        fontWeight: 600,
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
      startIcon: {
        marginRight: 8,
      },
      endIcon: {
        marginLeft: 8,
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.lg,
        border: `1px solid ${tokens.colors.grey[200]}`,
        boxShadow: 'none',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.lg,
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: {
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: tokens.colors.grey[500],
        backgroundColor: tokens.colors.grey[50],
        borderBottom: `1px solid ${tokens.colors.grey[200]}`,
      },
      body: {
        fontSize: '14px',
        color: tokens.colors.grey[700],
        borderBottom: `1px solid ${tokens.colors.grey[100]}`,
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:hover': {
          backgroundColor: tokens.colors.primary[50],
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: tokens.radius.sm,
        },
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: tokens.radius.lg,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: '20px',
        fontWeight: 500,
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: {
        height: 2,
        borderRadius: '2px 2px 0 0',
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '14px',
      },
    },
  },
  MuiAccordion: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.lg,
        '&:before': {
          display: 'none',
        },
        '&.Mui-expanded': {
          margin: 0,
        },
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontSize: '12px',
        borderRadius: tokens.radius.sm,
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.md,
      },
    },
  },
  MuiAvatar: {
    styleOverrides: {
      root: {
        fontWeight: 600,
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.sm,
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRadius: 0,
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        borderRadius: 0,
        boxShadow: 'none',
        borderBottom: `1px solid ${tokens.colors.grey[200]}`,
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: tokens.colors.grey[200],
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.md,
        '&.Mui-selected': {
          backgroundColor: tokens.colors.primary[50],
          color: tokens.colors.primary[700],
          '&:hover': {
            backgroundColor: tokens.colors.primary[100],
          },
        },
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.sm,
        margin: '2px 4px',
        '&.Mui-selected': {
          backgroundColor: tokens.colors.primary[50],
        },
      },
    },
  },
  MuiFilledInput: {
    styleOverrides: {
      root: {
        backgroundColor: lightModeColors.background.paper,
        '&:hover': {
          backgroundColor: tokens.colors.grey[50],
        },
        '&.Mui-focused': {
          backgroundColor: tokens.colors.grey[100],
        },
        '&:hover:not(.Mui-disabled):before': {
          borderBottom: `1px solid ${tokens.colors.grey[400]}`,
        },
      },
    },
  },
});

// Dark mode component overrides
const getDarkComponents = (): ThemeOptions['components'] => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: darkModeColors.background.default,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: tokens.colors.grey[600],
          borderRadius: '4px',
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.sm,
        padding: '8px 16px',
        fontSize: '13px',
        fontWeight: 600,
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
      startIcon: {
        marginRight: 8,
      },
      endIcon: {
        marginLeft: 8,
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.lg,
        border: `1px solid ${darkModeColors.border.primary}`,
        boxShadow: 'none',
        backgroundColor: darkModeColors.background.paper,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.lg,
        backgroundImage: 'none',
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: {
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: tokens.colors.grey[400],
        backgroundColor: darkModeColors.background.paper,
        borderBottom: `1px solid ${darkModeColors.border.primary}`,
      },
      body: {
        fontSize: '14px',
        color: darkModeColors.text.secondary,
        borderBottom: `1px solid ${darkModeColors.border.primary}`,
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:hover': {
          backgroundColor: darkModeColors.hover,
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: tokens.radius.sm,
        },
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: tokens.radius.lg,
        backgroundColor: darkModeColors.background.paper,
        backgroundImage: 'none',
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: '20px',
        fontWeight: 500,
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: {
        height: 2,
        borderRadius: '2px 2px 0 0',
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '14px',
      },
    },
  },
  MuiAccordion: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.lg,
        backgroundColor: darkModeColors.background.paper,
        '&:before': {
          display: 'none',
        },
        '&.Mui-expanded': {
          margin: 0,
        },
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontSize: '12px',
        borderRadius: tokens.radius.sm,
        backgroundColor: darkModeColors.background.paper,
        border: `1px solid ${darkModeColors.border.primary}`,
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.md,
      },
    },
  },
  MuiAvatar: {
    styleOverrides: {
      root: {
        fontWeight: 600,
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.sm,
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRadius: 0,
        backgroundColor: darkModeColors.background.paper,
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        borderRadius: 0,
        boxShadow: 'none',
        borderBottom: `1px solid ${darkModeColors.border.primary}`,
        backgroundColor: darkModeColors.background.paper,
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: darkModeColors.border.primary,
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.md,
        '&.Mui-selected': {
          backgroundColor: 'rgba(61, 139, 149, 0.2)',
          color: tokens.colors.primary[300],
          '&:hover': {
            backgroundColor: 'rgba(61, 139, 149, 0.3)',
          },
        },
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.sm,
        margin: '2px 4px',
        '&.Mui-selected': {
          backgroundColor: 'rgba(61, 139, 149, 0.2)',
        },
      },
    },
  },
  MuiFilledInput: {
    styleOverrides: {
      root: {
        backgroundColor: darkModeColors.background.paper,
        '&:hover': {
          backgroundColor: '#262626', // #1E1E1E + 4% white overlay
        },
        '&.Mui-focused': {
          backgroundColor: '#2D2D2D', // #1E1E1E + 8% white overlay
        },
        '&:hover:not(.Mui-disabled):before': {
          borderBottom: `1px solid ${darkModeColors.border.secondary}`,
        },
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      filled: {
        backgroundColor: darkModeColors.background.paper,
      },
    },
  },
});

// Factory function to create theme based on mode
export const createAppTheme = (mode: 'light' | 'dark') => {
  return mode === 'dark'
    ? createTheme(getDarkThemeOptions())
    : createTheme(getLightThemeOptions());
};

// Default theme (light) for backwards compatibility
export const theme = createAppTheme('light');
export default theme;
